"use client";

/**
 * RootBrandGroupGuard
 *
 * Mounted in the root layout (always alive).  Enforces the brand-group
 * navigation boundary whenever the app runs in standalone (PWA) mode with a
 * recorded installing slug.
 *
 * Why here instead of [slug]/layout?
 * When the user navigates from /{slug} to /admin, Next.js unmounts the [slug]
 * layout before the guard can fire — this root-level component stays mounted
 * for the entire session and intercepts every navigation regardless of which
 * route is active.
 *
 * O bloqueio e reavaliado a cada navegacao (leitura fresca do localStorage),
 * para que destravar o app — login de admin ou saida de emergencia — valha na
 * hora, sem precisar recarregar.
 *
 * ADR-002, ADR-003.
 */

import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { isStandaloneMode, checkNavigation } from "../../lib/brandGroupGuard";
import {
  getInstallingSlug,
  isAdminDevice,
  markBrandGuardBlocked,
} from "../../lib/installProfile";

export function RootBrandGroupGuard() {
  const router = useRouter();

  useEffect(() => {
    // Guard is a strict no-op outside standalone mode.
    if (!isStandaloneMode()) return;

    // Dispositivo de administrador nunca e travado no app do cliente.
    if (isAdminDevice()) return;

    async function guard(pathname: string) {
      // Leitura fresca: o bloqueio pode ter sido apagado nesta mesma sessao.
      const installingSlug = getInstallingSlug();
      if (!installingSlug) return;
      if (isAdminDevice()) return;

      const result = await checkNavigation(pathname, installingSlug);
      if (result === "redirect") {
        markBrandGuardBlocked();
        router.replace(`/${installingSlug}`);
      }
    }

    function currentTarget() {
      return `${window.location.pathname}${window.location.search}`;
    }

    // Guard the current page immediately on mount (covers hard navigations /
    // direct URL access in standalone mode).
    void guard(currentTarget());

    function handlePopState() {
      void guard(currentTarget());
    }

    const originalPushState = window.history.pushState.bind(window.history);
    const originalReplaceState = window.history.replaceState.bind(window.history);

    function targetFromArgs(url: string | URL) {
      if (typeof url === "string") {
        const parsed = new URL(url, window.location.origin);
        return `${parsed.pathname}${parsed.search}`;
      }
      return `${url.pathname ?? window.location.pathname}${url.search ?? ""}`;
    }

    function patchedPushState(...args: Parameters<typeof window.history.pushState>) {
      originalPushState(...args);
      const url = args[2];
      if (url) void guard(targetFromArgs(url));
    }

    function patchedReplaceState(
      ...args: Parameters<typeof window.history.replaceState>
    ) {
      originalReplaceState(...args);
      const url = args[2];
      if (url) void guard(targetFromArgs(url));
    }

    window.history.pushState = patchedPushState;
    window.history.replaceState = patchedReplaceState;
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener("popstate", handlePopState);
    };
  }, [router]);

  return null;
}
